import {
  Chip,
  ColorVariant,
  Flex,
  fromTheme,
  scale,
  scaleArbitrarily,
} from '@acsbe/ui';
import { FunctionComponent, ReactNode, useCallback, useMemo } from 'react';
import { styled } from 'styled-components';
import { useComboBoxContext } from './context';

export interface OptionProps {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  compact?: boolean;
  title?: string;
  status?: string;
  statusScale: ColorVariant;
}

export const Option: FunctionComponent<OptionProps> = ({
  value,
  label,
  icon,
  title,
  status,
  statusScale = 'base',
  disabled = false,
  compact = false,
}) => {
  const { selectedValues, setSelectedValues, multiselect, filter, raw } =
    useComboBoxContext();
  const shouldShow = useMemo(() => {
    return !filter || label.toLowerCase().includes(filter.toLowerCase());
  }, [filter, label]);
  const selected = useMemo(() => {
    return selectedValues.includes(value);
  }, [selectedValues, value]);
  const onSelect = useCallback(() => {
    if (selected) {
      setSelectedValues(selectedValues.filter((v) => v !== value));
    } else if (multiselect) {
      setSelectedValues([...selectedValues, value]);
    } else {
      setSelectedValues([value]);
    }
  }, [selected, selectedValues, setSelectedValues, value, multiselect]);

  if (raw) {
    return <li data-value={value}>{label}</li>;
  }

  if (!shouldShow) return null;

  return (
    <ListItem
      role="option"
      tabIndex={0}
      data-value={value}
      data-multi={multiselect}
      data-disabled={disabled}
      aria-selected={selected}
      data-compact={compact}
      onClick={onSelect}
    >
      {multiselect ? <div data-slot="checkbox" /> : null}
      <Flex as="span" row align="center" gap={2} minWidth="0px" stretch>
        {icon ? (
          <Flex
            width="24px"
            height="24px"
            radius="sm"
            border="xxs"
            align="center"
            justify="center"
          >
            {icon}
          </Flex>
        ) : null}
        <Flex align="flex-start" gap={2} stretch overflow="hidden">
          {title && <StyledSpan>{title}</StyledSpan>}
          <span>{label}</span>
        </Flex>
        {status && (
          <StatusChip scale={statusScale} small>
            <span>{status}</span>
          </StatusChip>
        )}
      </Flex>
    </ListItem>
  );
};

const StyledSpan = styled.span`
  font-weight: 600;
  line-height: 125%;
`;

const StatusChip = styled(Chip)`
  margin-inline-start: 16px;
  font-size: 600;
`;

const ListItem = styled.li`
  padding-inline: ${scale('lg')};
  padding-block: ${scale('md')};
  box-sizing: border-box;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${scale('md')};
  font-size: ${fromTheme('font.baseSize')};
  color: ${fromTheme('palette.scales.base.text.static')};
  border-radius: ${fromTheme('radii.md')};

  &:hover {
    background-color: ${fromTheme(
      'palette.scales.base.background.elementHovered',
    )};
  }

  &[aria-selected='true']:not([data-multi='true']),
  &:active {
    background-color: ${fromTheme(
      'palette.scales.base.background.elementActive',
    )};
  }

  &[data-disabled='true'] {
    pointer-events: none;
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${fromTheme('palette.focusColor')};
  }

  > span > div > span {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: var(--text-wrap);
  }

  &[data-compact='true'] {
    gap: ${scale('md')};
    line-height: 1;
  }

  &[data-multi='true'] {
    justify-content: start;
    gap: ${scale('lg')};

    &[data-compact='true'] {
      & > [data-slot='checkbox'] {
        width: ${scaleArbitrarily(4)};
      }

      span {
        font-size: calc(${fromTheme('font.baseSize')} - 2px);
      }
    }

    & > [data-slot='checkbox'] {
      --stroke: ${fromTheme('palette.scales.base.border.elementHovered')};
      color: ${fromTheme('palette.scales.primary.text.solid')};
      aspect-ratio: 1;
      box-sizing: border-box;
      width: ${scaleArbitrarily(5)};
      position: relative;

      &::before {
        content: '';
        display: block;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        border-radius: ${fromTheme('radii.sm')};
        box-shadow: 0 0 0 2px inset var(--stroke);
        background-color: var(--fill);
      }
    }

    &[aria-selected='true'] > [data-slot='checkbox'] {
      &::before {
        --fill: ${fromTheme('palette.scales.primary.background.solid')};
        --stroke: transparent;
      }

      &::after {
        content: '';
        display: block;
        background-color: currentColor;
        clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
        position: absolute;
        inset: 25%;
        transform: rotate(10deg);
      }
    }
  }
`;
